/**
 * Extraction d'un message d'erreur lisible depuis une erreur axios / fetch / JS.
 *
 * Gère :
 *  - detail chaîne (HTTPException classique)
 *  - detail objet { message, ... } (ex. 409 chevauchement de congés)
 *  - detail tableau FastAPI 422 ([{ loc, msg, type }])
 *  - erreurs réseau / timeout (pas de réponse)
 *
 * Ne renvoie JAMAIS un objet : le résultat peut être passé tel quel à toast.error()
 * sans provoquer l'erreur React "Objects are not valid as a React child".
 */

const STATUS_MESSAGES = {
  401: 'Session expirée, veuillez vous reconnecter.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'Ressource introuvable.',
  409: 'Conflit avec des données existantes.',
  413: 'Fichier trop volumineux.',
  422: 'Données invalides.',
  429: 'Trop de requêtes, veuillez patienter quelques instants.',
  500: 'Erreur interne du serveur.',
  502: 'Serveur indisponible, veuillez réessayer.',
  503: 'Service temporairement indisponible.',
  504: 'Le serveur met trop de temps à répondre.'
};

function formatValidationItem(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item !== 'object') return String(item);
  const msg = item.msg || item.message || '';
  const loc = Array.isArray(item.loc)
    ? item.loc.filter((l) => l !== 'body' && l !== 'query' && l !== 'path' && typeof l !== 'number').join('.')
    : '';
  if (loc && msg) return `${loc} : ${msg}`;
  return msg || loc;
}

/**
 * Convertit un "detail" (ou tout payload d'erreur) en chaîne lisible. Renvoie '' si rien d'exploitable.
 */
export function detailToString(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail.trim();
  if (typeof detail === 'number' || typeof detail === 'boolean') return String(detail);
  if (Array.isArray(detail)) {
    return detail.map(formatValidationItem).filter(Boolean).join(' ; ');
  }
  if (typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message.trim()) {
      let text = detail.message.trim();
      if (detail.solde && typeof detail.solde.message === 'string') {
        text += ` ${detail.solde.message}`;
      }
      return text;
    }
    if (detail.detail !== undefined && detail.detail !== detail) return detailToString(detail.detail);
    if (typeof detail.error === 'string') return detail.error;
    if (typeof detail.msg === 'string') return detail.msg;
    return '';
  }
  return '';
}

/**
 * Renvoie toujours une chaîne lisible pour l'utilisateur.
 * @param {any} error   - erreur axios (error.response.data.detail), Error JS ou chaîne
 * @param {string} fallback - message par défaut
 */
export function getErrorMessage(error, fallback = 'Une erreur est survenue') {
  if (!error) return fallback;
  if (typeof error === 'string') return error || fallback;

  const response = error.response;
  if (!response) {
    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
      return 'Le serveur met trop de temps à répondre. Veuillez réessayer.';
    }
    if (error.request || error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      return 'Impossible de joindre le serveur. Vérifiez votre connexion.';
    }
    const plain = detailToString(error);
    if (plain) return plain;
    return (typeof error.message === 'string' && error.message) || fallback;
  }

  const data = response.data;
  let text = '';
  if (data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)) {
    text = detailToString(data.detail !== undefined ? data.detail : data);
  } else if (typeof data === 'string') {
    // Une page HTML d'erreur (proxy, nginx) n'est pas un message exploitable
    text = /<\s*html/i.test(data) ? '' : data.trim();
  }
  if (text) return text;
  return STATUS_MESSAGES[response.status] || fallback;
}

export default getErrorMessage;
